import React from "react";
import { Checkbox, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";
import TableComponent from "../../components/table/list.jsx";
import { CustomTooltip } from "../../consts.js";
import { DATE_FORMAT } from "../../helpers/constant.js";
import Utils from "../../utils.js";
import HighALertConfirmButton from "./high-risk-confirm.jsx";
import { useThemeMode } from "../../components/theme/index.jsx";
import ReadAlertConfirmComponent from "./read-confirm.jsx";
import { AppDataContext } from "../../components/context/app-data-context.jsx";
import { removeDetectKeywordFromVisuals } from "../../helpers/index.js";

function Alerts() {
    const theme = useTheme();
    const navigate = useNavigate();
    const [selected, setSelected] = React.useState([]);
    const [unreadAlerts, setUnreadAlerts] = React.useState([]);
    const { colours } = useThemeMode();
    const { currentCompanyAlerts, setCurrentCompanyAlerts, loadCompanyAlertsData, loadingAlerts } = React.useContext(AppDataContext);

    async function getCompanyData() {
        Utils.sendProgressBar(true);
        if(currentCompanyAlerts.length === 0 && !loadingAlerts) {
            await loadCompanyAlertsData();
        }
    }

    // Reset selected when data is loaded
    // This will reset the selected state when the data is loaded
    React.useEffect(() => {
        if(!loadingAlerts) {
            setSelected([]);
            if (currentCompanyAlerts.length > 0) {
                const _unreadAlerts = currentCompanyAlerts.filter(item => !item.isRead);
                setUnreadAlerts(_unreadAlerts);
            }
        }
    }, [currentCompanyAlerts, loadingAlerts]);

    // This will load the data when the component mounts
    // and when the loadingAlerts state changes
    React.useEffect(() => {
        getCompanyData(); // Your async data fetching function
    }, []);

    const handleSelect = (checked, selectAll) => {
        if (typeof selectAll === 'boolean') {
            let selectedData = [];
            if (checked) {
                selectedData = currentCompanyAlerts.map((d) => d.id)
            }
            setSelected(selectedData);

        } else {
            let selectedData = [...selected];
            if (selected.includes(selectAll.id)) {
                selectedData = selectedData.filter(d => d !== selectAll.id);
            } else {
                selectedData.push(selectAll.id);
            }
            setSelected(selectedData);
        }
    }

    // define table headers
    const columns = [
        {
            label: (<CustomTooltip title={unreadAlerts.length === 0 ? "No unread alerts" : "Select All Unread Alerts"}
                placement="right">
                <span>
                    <Checkbox
                        indeterminate={selected.length > 0 && selected.length < unreadAlerts.length}
                        checked={unreadAlerts.length > 0 && unreadAlerts.every((c) => selected.includes(c.id))}
                        onChange={(e) => handleSelect(e.target.checked, true)}
                        disabled={currentCompanyAlerts.length === 0 || unreadAlerts.length === 0}
                        inputProps={{ 'aria-label': unreadAlerts.length === 0 ? "No unread alerts" : "Select All Unread Alerts" }}
                    />
                </span>
            </CustomTooltip>),
            key: "",
            sortable: false,
            cellClassName: (row) => row.isRead ? "read" : "unread",
            cellStyle: {
                "&.unread": {
                    backgroundColor: theme.palette.primary.main + "1A",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        backgroundColor: theme.palette.primary.main,
                        width: 4,
                        height: "100%"
                    },
                },
            },
            renderItem: (row) => (<CustomTooltip title={"Select to Mark as Read"} placement="right">
                <span>
                    <Checkbox
                        checked={selected.includes(row.id)}
                        onChange={(e) => handleSelect(e.target.checked, row)}
                        // disabled={row.isRead}
                        inputProps={{ 'aria-label': `Select ${row.name} to Mark as Read` }}
                    />
                </span>
            </CustomTooltip>)
        },
        {
            label: "Insight Name",
            key: "name",
            sortable: true,
            cellClassName: (row) => row.isRead ? "read" : "unread",
            cellStyle: {
                "&.unread": {
                    backgroundColor: theme.palette.primary.main + "1A",
                },
            },
            renderItem: (row) => <Typography fontWeight={row.isRead ? 400 : 600}>{removeDetectKeywordFromVisuals(row.name) || "-"}</Typography>,
        },
        {
            label: "Agent",
            key: "agent",
            sortable: true,
            cellClassName: (row) => row.isRead ? "read" : "unread",
            cellStyle: {
                "&.unread": {
                    backgroundColor: theme.palette.primary.main + "1A",
                },
            },
            renderItem: (row) => row.agent || "-",
        },
        {
            label: "Campaigns",
            key: "campaign",
            sortable: true,
            cellClassName: (row) => row.isRead ? "read" : "unread",
            cellStyle: {
                "&.unread": {
                    backgroundColor: theme.palette.primary.main + "1A",
                },
            },
            renderItem: (row) => row.campaign || "-",
        },
        {
            label: "Conversation",
            key: "conversation",
            sortable: true,
            cellClassName: (row) => row.isRead ? "read" : "unread",
            cellStyle: {
                "&.unread": {
                    backgroundColor: theme.palette.primary.main + "1A",
                },
            },
            renderItem: (row) => (<Stack
                direction="row"
                alignItems="center"
                gap={1}
            >
                <CustomTooltip title={"View Details"}>
                    <IconButton color="primary"
                        onClick={() =>
                            navigate("/conversation?id=" + row.conversation)
                        }
                        sx={colours.shadowMd}
                        aria-label="Play"
                    >
                        <FeatherIcon icon="play" />
                    </IconButton>
                </CustomTooltip>
                {row.conversation}
            </Stack>)
        },
        {
            label: "High Risk",
            key: "risk",
            sortable: true,
            cellClassName: (row) => row.isRead ? "read" : "unread",
            cellStyle: {
                "&.unread": {
                    backgroundColor: theme.palette.primary.main + "1A",
                },
            },
            renderItem: (row) => (<HighALertConfirmButton
                alertObj={row}
                onSuccess={(update) => {
                    for (const item of currentCompanyAlerts) {
                        if(item.id === update.id) {
                            item.risk = update.risk;
                            break;
                        }
                    }
                    setCurrentCompanyAlerts([...currentCompanyAlerts]);
                }}
            />)
        },
        {
            label: "Date",
            key: 'createdAt',
            sortable: true,
            cellClassName: (row) => row.isRead ? "read" : "unread",
            cellStyle: {
                "&.unread": {
                    backgroundColor: theme.palette.primary.main + "1A",
                },
            },
            renderItem: (row) => (<>
                {format(row.createdAt, DATE_FORMAT)}
            </>)
        }
    ];

    const headerActionButtons = () => {
        return (<ReadAlertConfirmComponent
            selected={selected.length ? selected : unreadAlerts.map((d) => d.id)}
            unreadCounts={unreadAlerts.length}
            onSuccess={() => {
                setSelected([]);
                loadCompanyAlertsData(false);
            }}
        />)
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <Grid
                className="vocalysed-home-upadted alerts"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    headerActionButtons={headerActionButtons}
                    spinner={loadingAlerts}
                    data={currentCompanyAlerts}
                    columns={columns}
                    pagination={true}
                    rowStyle={{ position: 'relative' }}
                    searchKeys={["name", "agent", "campaign"]}
                    defaultOptions={{ sortKey: "risk", sortOrder: "asc" }}
                    noDataMessage={"No alerts found."}
                />
            </Grid>
        </Grid>
    );
}

export default Alerts;
